import { ContentLink } from "@robe/client";
import { Box, Flex, Icon, Img, Link as UILink, Modal, Tag, Typography } from "@xcorejs/ui";
import ContactIcon from "components/Icons/16/contact.svg";
import GlobeIcon from "components/Icons/16/globe.svg";
import NewsletterIcon from "components/Icons/16/newsletter.svg";
import { Heading3 } from "design-system";
import { useLayout } from "xcore";
import { Rental } from "xcore/types";
import Richtext from "../../design-system/Richtext";

interface RentalFinderModalProps {
  rental: Rental;
  getDirections?: string;
}

export const RentalFinderModal = ({ rental, getDirections }: RentalFinderModalProps) => {
  const { stringify, image: imageFunc } = useLayout();

  const phones = stringify(rental.values.phone)!
    .split(",")
    .map((p) => p.trim());
  const emails = stringify(rental.values.email)!
    .split(",")
    .map((p) => p.trim());
  const websites = stringify(rental.values.web)!
    .split(",")
    .map((p) => p.trim());

  const image = rental?.values?.image ? imageFunc(rental.values.image, { width: 300, height: 300 }) : null;

  return (
    <Modal size="lg" padding={{ _: "2rem", md: "4rem" }} _overlay={{ zIndex: 5 }}>
      <Flex alignItems="flex-start" flexDirection={{ _: "column-reverse", sm: "row" }}>
        {image && (
          <Flex flex="0 0 auto" mr={{ _: 0, sm: "3rem" }} mt={{ _: "3rem", sm: 0 }}>
            <Img src={image} alt="" maxWidth="240px" />
          </Flex>
        )}

        <Box>
          <Heading3>{stringify(rental.values.title)}</Heading3>

          {rental.values.products && (
            <Flex mt="1rem" flexWrap="wrap" style={{ gap: "1rem" }}>
              {rental.values.products.map((p) => {
                if (!p.id || !p.type || !p.slug) {
                  return null;
                }

                return (
                  <ContentLink key={p.id} content={{ id: p.id, type: p.type, slug: p.slug }} target="_blank">
                    <Tag bg="solidWhite" color="black" border="0">
                      {stringify(p.title)}
                    </Tag>
                  </ContentLink>
                );
              })}
            </Flex>
          )}

          <Flex mt={{ _: "1rem", sm: "3rem" }} flexDirection={{ _: "column", sm: "row" }}>
            <Flex mr={{ _: 0, sm: "5.6rem" }} mb={{ _: "2rem", sm: 0 }} flexDirection="column" alignItems="flex-start" flexGrow={1}>
              <Typography as="div" style={{ whiteSpace: "pre-wrap" }}>
                <Richtext value={rental.values.address} />
              </Typography>

              <UILink
                v="underline"
                maxWidth="100%"
                mt="1rem"
                href={rental.values.directions || getGoogleMapsLink(rental.values.latitude!, rental.values.longitude!)}
                target="_blank"
                rel="noreferrer"
              >
                {getDirections}
              </UILink>
            </Flex>

            <Flex flexDirection="column" {...{ style: { gap: "1rem" } }} flexGrow={1}>
              {phones.map(
                (p, i) =>
                  p && (
                    <UILink key={i} v="simple" href={"tel:" + p} target="_blank">
                      <Icon svg={<ContactIcon />} _icon={{ fill: "black" }} mr="1rem" /> {p}
                    </UILink>
                  )
              )}

              {emails.map(
                (e, i) =>
                  e && (
                    <UILink key={i} v="simple" href={"mailto:" + e} target="_blank">
                      <Icon svg={<NewsletterIcon />} _icon={{ fill: "black" }} mr="1rem" /> {e}
                    </UILink>
                  )
              )}

              {websites.map(
                (w, i) =>
                  w && (
                    <UILink key={i} v="simple" href={w} target="_blank" rel="noreferrer">
                      <Icon svg={<GlobeIcon />} _icon={{ fill: "black" }} mr="1rem" /> {w}
                    </UILink>
                  )
              )}
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Modal>
  );
};

const getGoogleMapsLink = (lat: string, long: string) => `https://www.google.cz/maps/place/${toDegFormat(+lat)}N+${toDegFormat(+long)}E`;

const toDegFormat = (num: number) => {
  const deg = Math.floor(num);
  const min = Math.floor((num - deg) * 60) / 60;
  const sec = Math.round((num - deg - min) * 10 * 60 * 60) / 10;

  return `${deg}°${min * 60}'${sec}"`;
};
